import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Send, Users, ShieldCheck, DollarSign, Clock, CheckCircle2, XCircle, Ban, Download } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Founder-only "Pay Staff" modal. Kalib/John picks staff, sets amounts,
 * types their signature, and creates a payroll_runs record. The founder
 * then executes the ACH pushes in their real bank UI and can flip the
 * run to `sent` here for the audit trail.
 */
export default function PayStaffModal({ onClose, sourceBank, onRunCreated }) {
  const [staff, setStaff] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [memos, setMemos] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signature, setSignature] = useState("");
  const [periodLabel, setPeriodLabel] = useState(() => {
    const d = new Date();
    return `${d.toLocaleString(undefined, { month: "short", year: "numeric" })}`;
  });
  const [notes, setNotes] = useState("");
  // Multi-bank picker: [{ founder_id, username, bank_id, bank_name, bank_last4, bank_account_type, is_primary, is_self }]
  const [bankOptions, setBankOptions] = useState([]);
  const [pickedBank, setPickedBank] = useState(null);   // { founder_id, bank_id }

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: staffData }, { data: runsData }, { data: bankData }] = await Promise.all([
        api.get("/admin-auth/staff"),
        api.get("/admin-auth/payroll/runs?limit=8"),
        api.get("/admin-auth/founder-banks"),
      ]);
      const eligible = (staffData.staff || []).filter(
        (s) => s.role !== "founder" && s.onboarding_status === "finalized" && s.status !== "terminated",
      );
      setStaff(eligible);
      setRuns(runsData.runs || []);

      // Flatten every founder's banks into a picker list.
      const opts = [];
      for (const f of (bankData.founders || [])) {
        for (const b of (f.banks || [])) {
          opts.push({
            founder_id:   f.founder_id,
            username:     f.username,
            display_name: f.display_name || f.username,
            bank_id:      b.id,
            bank_name:    b.bank_name,
            bank_last4:   b.bank_last4,
            bank_account_type: b.bank_account_type,
            is_primary:   b.is_primary,
            is_self:      f.is_self,
            fc_linked:    b.fc_linked,
          });
        }
      }
      setBankOptions(opts);
      // Default pick: self-primary → any-primary → first available.
      if (!pickedBank && opts.length > 0) {
        const selfPrimary = opts.find((o) => o.is_self && o.is_primary);
        const anyPrimary  = opts.find((o) => o.is_primary);
        const chosen = selfPrimary || anyPrimary || opts[0];
        setPickedBank({ founder_id: chosen.founder_id, bank_id: chosen.bank_id });
      }
    } catch (e) {
      toast.error("Could not load staff roster.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Derived: currently selected bank details for the header + submit payload.
  const pickedBankMeta = useMemo(() => {
    if (!pickedBank) return null;
    return bankOptions.find(
      (b) => b.founder_id === pickedBank.founder_id && b.bank_id === pickedBank.bank_id,
    ) || null;
  }, [bankOptions, pickedBank]);

  const total = useMemo(() => {
    let cents = 0;
    for (const s of staff) {
      if (!selected.has(s.id)) continue;
      const dollars = Number((amounts[s.id] || "0").toString().replace(/[^\d.]/g, "")) || 0;
      cents += Math.round(dollars * 100);
    }
    return cents;
  }, [selected, amounts, staff]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Select at least one staff member."); return; }
    if (!signature.trim()) { toast.error("Type your legal name to authorize."); return; }
    const entries = [];
    for (const s of staff) {
      if (!selected.has(s.id)) continue;
      const dollars = Number((amounts[s.id] || "0").toString().replace(/[^\d.]/g, "")) || 0;
      if (dollars <= 0) { toast.error(`Enter an amount for ${s.display_name || s.username}.`); return; }
      entries.push({
        user_id: s.id,
        amount_cents: Math.round(dollars * 100),
        memo: (memos[s.id] || "").trim() || null,
      });
    }
    setSaving(true);
    try {
      await api.post("/admin-auth/payroll/runs", {
        entries,
        authorization_signature: signature.trim(),
        period_label: periodLabel.trim() || null,
        notes: notes.trim() || null,
        source_bank_id:    pickedBank?.bank_id || null,
        source_founder_id: pickedBank?.founder_id || null,
      });
      toast.success(`Authorized ${entries.length} payment${entries.length === 1 ? "" : "s"}. Recipients emailed.`);
      // Reset local state, refresh recent runs, notify parent.
      setSelected(new Set());
      setAmounts({});
      setMemos({});
      setSignature("");
      setNotes("");
      onRunCreated?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not authorize payroll.");
    } finally { setSaving(false); }
  };

  const markSent = async (runId) => {
    try {
      await api.post(`/admin-auth/payroll/runs/${runId}/mark-sent`);
      toast.success("Marked as sent.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark sent."); }
  };
  const voidRun = async (runId) => {
    if (!window.confirm("Void this authorized run? (Only works before it's been sent.)")) return;
    try {
      await api.post(`/admin-auth/payroll/runs/${runId}/void`);
      toast.success("Voided.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not void."); }
  };

  const downloadRunCsv = async (run) => {
    try {
      const { data } = await api.get(`/admin-auth/payroll/runs/${run.id}.csv`, { responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safePeriod = (run.period_label || "run").replace(/[^\w-]/g, "_");
      a.download = `elevare-payroll-${safePeriod}-${run.id.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded — upload to your bank's batch payment tool.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download CSV.");
    }
  };

  return (
    <div data-testid="pay-staff-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#eae7de] shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#eae7de]/95 backdrop-blur-md px-6 pt-5 pb-3 border-b border-[#1e3d3a]/10 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f] flex items-center gap-1.5">
              <Send className="h-3 w-3" /> Founder payroll
            </p>
            <h2 className="font-head text-2xl mt-1 text-[#1e3d3a]">Pay staff</h2>
            {/* Source-bank picker: any linked founder bank across Kalib + John. */}
            {bankOptions.length > 0 ? (
              <div className="mt-2" data-testid="pay-staff-source-picker">
                <label className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mr-2">Source</label>
                <select
                  data-testid="pay-staff-source-select"
                  value={pickedBank ? `${pickedBank.founder_id}|${pickedBank.bank_id}` : ""}
                  onChange={(e) => {
                    const [fid, bid] = (e.target.value || "").split("|");
                    setPickedBank(fid && bid ? { founder_id: fid, bank_id: bid } : null);
                  }}
                  className="text-[11px] rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                >
                  {bankOptions.map((b) => (
                    <option key={`${b.founder_id}|${b.bank_id}`} value={`${b.founder_id}|${b.bank_id}`}>
                      {b.bank_name || "Bank"} ····{b.bank_last4 || "----"} · {b.bank_account_type || "checking"}
                      {b.is_primary ? " ★" : ""} · @{b.username}{b.fc_linked ? " · FC" : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="text-[11px] text-[#b23a48] mt-1">
                No founder bank on file. Add one (or have your co-founder add theirs) to authorize payroll.
              </p>
            )}
          </div>
          <button
            type="button"
            data-testid="pay-staff-close"
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-[#1e3d3a]/8 text-[#1e3d3a]/55 hover:text-[#1e3d3a]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Period label + notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Pay period label</label>
              <input
                data-testid="pay-staff-period"
                type="text"
                value={periodLabel}
                onChange={(e) => setPeriodLabel(e.target.value)}
                placeholder="e.g. 2026-08 · P1"
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Internal notes (optional)</label>
              <input
                data-testid="pay-staff-notes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Includes performance bonuses"
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
              />
            </div>
          </div>

          {/* Staff list */}
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
            <div className="px-4 py-2.5 bg-[#eae7de] border-b border-[#1e3d3a]/8 flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-[#a3823f]" />
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Eligible staff</p>
              <span className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/45 ml-auto">
                {staff.length} finalized · with bank on file
              </span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-[#c9a961]" />
              </div>
            ) : staff.length === 0 ? (
              <p className="text-[13px] text-[#1e3d3a]/45 py-6 text-center">
                No finalized hires yet — approve + finalize a staff member first.
              </p>
            ) : (
              <ul className="divide-y divide-[#1e3d3a]/6" data-testid="pay-staff-list">
                {staff.map((s) => {
                  const isSel = selected.has(s.id);
                  const last4 = s.bank_last4 || s.onboarding_profile?.bank_account?.slice?.(-4) || "----";
                  return (
                    <li key={s.id} data-testid={`pay-row-${s.username}`} className="p-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`pay-select-${s.username}`}
                          checked={isSel}
                          onChange={() => toggle(s.id)}
                          className="mt-1.5 h-4 w-4 accent-[#a3823f]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-[13px] font-medium text-[#1e3d3a]">{s.display_name || s.username}</p>
                            <span className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/45">
                              @{s.username}
                            </span>
                          </div>
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 truncate">
                            {s.onboarding_profile?.bank_name || "—"} · ••••{last4}
                          </p>
                        </div>
                        {isSel && (
                          <div className="flex flex-col md:flex-row items-end gap-2 shrink-0">
                            <div className="relative">
                              <DollarSign className="h-3.5 w-3.5 text-[#a3823f] absolute left-2 top-1/2 -translate-y-1/2" />
                              <input
                                data-testid={`pay-amount-${s.username}`}
                                type="text"
                                inputMode="decimal"
                                value={amounts[s.id] || ""}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setAmounts({ ...amounts, [s.id]: e.target.value.replace(/[^\d.]/g, "") })}
                                placeholder="0.00"
                                className="w-28 pl-6 pr-2 py-1.5 rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de] text-sm text-right tabular-nums focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                              />
                            </div>
                            <input
                              data-testid={`pay-memo-${s.username}`}
                              type="text"
                              value={memos[s.id] || ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setMemos({ ...memos, [s.id]: e.target.value })}
                              placeholder="Memo (optional)"
                              className="w-40 px-2 py-1.5 rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de] text-xs focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                            />
                          </div>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="px-4 py-3 bg-[#eae7de]/50 border-t border-[#1e3d3a]/8 flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/60">
                {selected.size} selected
              </p>
              <p className="text-[15px] tabular-nums font-medium text-[#a3823f]">
                Total: {money(total)}
              </p>
            </div>
          </div>

          {/* Authorization */}
          <div className="rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/8 p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-[#a3823f]" />
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Founder authorization</p>
            </div>
            <p className="text-[12px] text-[#1e3d3a]/70 mb-2 leading-relaxed">
              By typing your legal name below and clicking "Authorize payroll", you certify these
              payments are approved by Elevare Solutions. This creates an authorized payroll
              record — you'll still need to initiate the ACH transfers in your bank UI, then mark
              each run as <span className="italic">sent</span> below.
            </p>
            <input
              data-testid="pay-staff-signature"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Type your legal name to sign"
              className="w-full rounded-xl border border-[#c9a961]/40 bg-white px-4 py-3 font-head text-xl italic text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                data-testid="pay-staff-submit"
                onClick={submit}
                disabled={saving || bankOptions.length === 0 || selected.size === 0 || !signature.trim() || !pickedBank}
                className="inline-flex items-center gap-2 rounded-full bg-[#a3823f] text-white px-6 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Authorize payroll · {money(total)}
              </button>
            </div>
          </div>

          {/* Recent runs */}
          {runs.length > 0 && (
            <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="pay-staff-recent-runs">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-[#a3823f]" />
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Recent runs</p>
              </div>
              <ul className="divide-y divide-[#1e3d3a]/6">
                {runs.map((r) => (
                  <li key={r.id} data-testid={`pay-run-${r.id.slice(0,8)}`} className="py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[13px] text-[#1e3d3a] truncate">
                          {r.period_label} · <span className="tabular-nums text-[#a3823f]">{money(r.total_cents)}</span>
                          <span className="text-[9px] uppercase tracking-[0.22em] ml-2 text-[#1e3d3a]/45">
                            {r.entries.length} recipient{r.entries.length === 1 ? "" : "s"}
                          </span>
                        </p>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">
                          {new Date(r.authorized_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          · by {r.authorized_by}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <StatusChip status={r.status} />
                        <button
                          type="button"
                          data-testid={`pay-csv-${r.id.slice(0,8)}`}
                          onClick={() => downloadRunCsv(r)}
                          title="Download bank-batch-upload CSV"
                          className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/70 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:text-[#1e3d3a] hover:border-[#c9a961] transition-colors duration-300"
                        >
                          <Download className="h-3 w-3" /> CSV
                        </button>
                        {r.status === "authorized" && (
                          <>
                            <button
                              type="button"
                              data-testid={`pay-mark-sent-${r.id.slice(0,8)}`}
                              onClick={() => markSent(r.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/40 text-[#4d5a24] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#6b7c3a]/10 transition-colors duration-300"
                            >
                              <CheckCircle2 className="h-3 w-3" /> Sent
                            </button>
                            <button
                              type="button"
                              data-testid={`pay-void-${r.id.slice(0,8)}`}
                              onClick={() => voidRun(r.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-[#b23a48]/30 text-[#b23a48] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#b23a48]/10 transition-colors duration-300"
                            >
                              <Ban className="h-3 w-3" /> Void
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    authorized: { label: "Authorized", cls: "bg-[#c9a961]/15 text-[#a3823f]", Icon: Clock },
    sent:       { label: "Sent",       cls: "bg-[#6b7c3a]/15 text-[#4d5a24]", Icon: CheckCircle2 },
    voided:     { label: "Voided",     cls: "bg-[#b23a48]/10 text-[#b23a48]", Icon: XCircle },
  };
  const m = map[status] || map.authorized;
  return (
    <span data-testid={`pay-status-${status}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] ${m.cls}`}>
      <m.Icon className="h-3 w-3" />{m.label}
    </span>
  );
}

const money = (cents) => (Number(cents || 0) / 100).toLocaleString(undefined, {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
});
